/**
 * 深邃蓝模板 - 登录页
 * 设计：居中卡片 + 深蓝几何图案背景
 */

import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { siteConfig, loading: configLoading } = useSiteConfig();
  const from = (location.state as { from?: string })?.from || "/";

  // siteConfig加载前不显示默认名称
  const siteLogo = siteConfig?.logo || "";
  const siteDisplayName = siteConfig?.site_name || siteConfig?.name || "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!username.trim()) { setError("请输入用户名"); return; }
    if (!password.trim()) { setError("请输入密码"); return; }
    const result = await login(username, password);
    if (result.success) { navigate(from, { replace: true }); }
    else { setError(result.message || "登录失败"); }
  };

  // 站点配置未加载时不显示默认名称
  if (configLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0B1A2E] via-[#0F1D2F] to-[#162740] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1A2E] via-[#0F1D2F] to-[#162740] flex items-center justify-center px-4 py-8 relative overflow-hidden">
      {/* 网格纹理 */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'linear-gradient(#3B82F6 1px, transparent 1px), linear-gradient(90deg, #3B82F6 1px, transparent 1px)',
        backgroundSize: '50px 50px',
      }} />
      {/* 光效 */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-400/8 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600/5 rounded-full blur-3xl" />

      {/* 几何装饰 */}
      <div className="absolute top-20 left-16 w-16 h-16 border border-blue-500/15 rounded-lg rotate-12" />
      <div className="absolute bottom-24 right-20 w-20 h-20 border border-blue-400/10 rounded-full" />
      <div className="absolute top-1/3 right-28 w-8 h-8 bg-blue-500/10 rounded-md -rotate-6" />

      {/* 居中卡片 */}
      <div className="relative z-10 w-full max-w-[440px]">
        {/* Logo - 仅在配置加载完成后显示 */}
        {siteDisplayName && (
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2.5">
              {siteLogo && <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center p-1.5 overflow-hidden"><img src={siteLogo} alt={siteDisplayName} className="w-full h-full object-contain brightness-0 invert" /></div>}
              <span className="text-xl font-bold text-white">{siteDisplayName}</span>
            </Link>
          </div>
        )}

        {/* 登录卡片 */}
        <div className="bg-white rounded-2xl shadow-2xl shadow-black/20 overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-blue-600 via-blue-500 to-sky-400" />
          <div className="p-8">
            <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-blue-600 transition-colors mb-6">
              <i className="ri-arrow-left-line" />返回首页
            </Link>

            <h1 className="text-2xl font-bold text-gray-800 mb-1">欢迎回来</h1>
            <p className="text-sm text-gray-400 mb-8">登录您的账户以继续</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">用户名</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><i className="ri-user-3-line text-gray-400" /></div>
                  <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="请输入用户名"
                    className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">密码</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><i className="ri-lock-2-line text-gray-400" /></div>
                  <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="请输入密码"
                    className="w-full pl-11 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600">
                    <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"} />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 text-gray-500 cursor-pointer"><input type="checkbox" className="w-4 h-4 rounded accent-blue-600" />记住我</label>
                <Link to="/forgot-password" className="text-blue-600 hover:text-blue-700 font-medium">忘记密码？</Link>
              </div>
              {error && <div className="p-3 bg-red-50 text-red-600 text-sm rounded-xl flex items-center gap-2 border border-red-100"><i className="ri-error-warning-line shrink-0" />{error}</div>}
              <button type="submit" disabled={isLoading}
                className="w-full py-3.5 bg-[#0F1D2F] text-white font-semibold rounded-xl hover:bg-[#1a2d45] transition-all disabled:opacity-50 shadow-sm text-base">
                {isLoading ? <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin" />登录中...</span> : "登  录"}
              </button>
            </form>

            <div className="mt-6 text-center text-sm text-gray-500">
              还没有账户？<Link to="/register" className="text-blue-600 font-semibold hover:text-blue-700 ml-1">立即注册</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
